import * as React from 'react';

import Card, { CardSection } from '@lightspeed/cirrus-card';
import Text from '@lightspeed/cirrus-text';
import { Badge } from '@lightspeed/flame/Badge';
import { Divider } from '@lightspeed/flame/Divider';
import { IconArrowRight } from '@lightspeed/flame/Icon/ArrowRight';
import GuiDefHiddenInput from 'Autocomplete/GuiDefHiddenInput';
import ShopsAutocomplete from 'Autocomplete/Shops';

import Format from 'helpers/format';
import { createReactRenderer } from 'helpers/react-renderer';

const Transfer = ({
  transfer: {
    transferId,
    status = 'open',
    createdBy,
    sentDate,
    dateTimeFormat,
    fromShopId,
    toShopId,
  } = {},
  isDisabled,
  currentEmployeeId,
  currentShopId,
  defaultOptions,
} = {}) => {
  const getShop = (shopId) => defaultOptions.find(({ value }) => value === shopId);

  const [fromShop, setFromShop] = React.useState(getShop(fromShopId || currentShopId));
  const [toShop, setToShop] = React.useState(getShop(toShopId));
  const dateRef = React.useRef(null);
  const transferLabel = transferId ? `Transfer #${transferId}` : 'New Transfer';
  const { date } = sentDate || {};

  React.useEffect(() => {
    // Getting the gui-def time selector and re-assiging to the react container
    const children = [...document.getElementById('view_need_by').parentElement.childNodes];
    children.forEach((el) => el && dateRef.current.appendChild(el));

    if (!toShop && defaultOptions.length === 2) {
      setToShop(defaultOptions.find(({ value }) => value !== fromShop.value));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFromShopChange = (value) => {
    setFromShop(value);

    const { value: fromValue } = value || {};
    const { value: toValue } = toShop || {};

    if (fromValue && (fromValue === toValue || !toValue)) {
      setToShop(
        defaultOptions.length === 2
          ? defaultOptions.find((option) => option.value !== fromValue)
          : null
      );
    }

    window.viewFieldChanged('view');
  };

  const handleToShopChange = (value) => {
    setToShop(value);

    const { value: toValue } = value || {};
    const { value: fromValue } = fromShop || {};

    if (toValue && (toValue === fromValue || !fromValue)) {
      setFromShop(
        defaultOptions.length === 2
          ? defaultOptions.find((option) => option.value !== toValue)
          : null
      );
    }

    window.viewFieldChanged('view');
  };

  return (
    <div className="transfer-view">
      <div className="transfer-view__summary">
        <Card>
          <CardSection>
            <Text heading3 className="transfer-view__label">
              {transferLabel}
            </Text>
          </CardSection>
          <Divider />
          <CardSection>
            <div className="transfer-view__status">
              <div className="cr-mb-1">
                <Text bodySmall dimmed>
                  Status
                </Text>
              </div>
              {(status === 'open' || status === 'reopened') && <Badge bg="gray-100">open</Badge>}
              {status === 'sent' && (
                <Badge type="warning" color="orange-200">
                  sent
                </Badge>
              )}
              {status === 'received' && (
                <Badge type="info" bg="gray-300" color="black">
                  received
                </Badge>
              )}
            </div>
            {createdBy ? (
              <div className="transfer-view__created-by">
                <Text bodySmall dimmed>
                  Created by
                </Text>
                <Text body data-test="transfer-created-by">
                  {createdBy}
                </Text>
              </div>
            ) : (
              <GuiDefHiddenInput
                name="sent_by_employee_id"
                id="sent_by_employee_id"
                data-test="transfer-sent-by-employee"
                value={currentEmployeeId}
              />
            )}

            {date && (
              <div className="transfer-view__sent">
                <Text bodySmall dimmed>
                  Sent
                </Text>
                <Text body data-test="transfer-sent-date">
                  {Format.formatDateTime(date, dateTimeFormat)}
                </Text>
              </div>
            )}
          </CardSection>
        </Card>
      </div>
      <div className="transfer-view__details">
        <Card>
          <CardSection>
            <div className="transfer-view__shops">
              <div className="transfer-view__sending-shop">
                <div className="cr-mb-1">
                  <Text bodySmall>From / Sending shop</Text>
                </div>
                <ShopsAutocomplete
                  className="transfer-view__autocomplete"
                  placeholder="Choose a shop"
                  inputId="transfer-from-shop"
                  value={fromShop}
                  defaultOptions={defaultOptions}
                  isClearable
                  isDisabled={isDisabled}
                  onChange={handleFromShopChange}
                  hiddenInput={{
                    name: 'sending_shop_id',
                    id: 'sending_shop_id',
                    'data-test': 'transfer-from-shop',
                  }}
                />
              </div>
              <div className="transfer-view__shops-spacer">
                <IconArrowRight size="0.875rem" baseColor="#2875c6" />
              </div>
              <div className="transfer-view__receiving-shop">
                <div className="cr-mb-1">
                  <Text bodySmall>To / Receiving shop</Text>
                </div>
                <ShopsAutocomplete
                  className="transfer-view__autocomplete"
                  placeholder="Choose a shop"
                  inputId="transfer-to-shop"
                  value={toShop}
                  defaultOptions={defaultOptions}
                  isClearable
                  isDisabled={isDisabled}
                  onChange={handleToShopChange}
                  hiddenInput={{
                    name: 'receiving_shop_id',
                    id: 'receiving_shop_id',
                    'data-test': 'transfer-to-shop',
                  }}
                />
              </div>
            </div>
          </CardSection>
          <Divider />
          <CardSection>
            <div className="transfer-view__need-by">
              <div className="transfer-view__need-by-label">
                <Text bodySmall>Need by</Text>
              </div>
              <div className="transfer-view__need-by-date" ref={dateRef} />
            </div>
          </CardSection>
        </Card>
      </div>
    </div>
  );
};

export default Transfer;

const RenderedComponent = createReactRenderer({
  component: Transfer,
  selector: '[data-react-root="Transfer"]',
});

history.onChangeState(
  { name: 'transfer.views.transfer', id: undefined },
  () => {
    RenderedComponent.mount();
  },
  () => {
    RenderedComponent.unmount();
  }
);
